"use client";

import { Loader2, Mail, RefreshCw, Trash2, UserPlus } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { SettingsSection } from "@/components/settings/SettingsSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trackSharing } from "@/lib/analytics";
import { sharingSchemas, validate } from "@/lib/validation";

interface ReferralInfo {
  id: string;
  email: string;
  accepted: boolean;
  createdAt: string;
  acceptedAt?: string;
}

export function ReferralSection() {
  const t = useTranslations("referral");
  const [email, setEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [referrals, setReferrals] = useState<ReferralInfo[]>([]);
  const [isLoadingReferrals, setIsLoadingReferrals] = useState(true);

  const MAX_REFERRALS = 3;

  const fetchReferrals = async () => {
    setIsLoadingReferrals(true);
    try {
      const response = await fetch("/api/referrals");
      if (response.ok) {
        const data = await response.json();
        setReferrals(data.referrals || []);
      }
    } catch (error) {
      console.error("Error fetching referrals:", error);
    } finally {
      setIsLoadingReferrals(false);
    }
  };

  // Fetch referrals on mount
  useEffect(() => {
    fetchReferrals();
  }, []);

  const handleSendReferral = async () => {
    const trimmedEmail = email.trim();

    // Validate email
    const validationResult = validate(sharingSchemas.email, trimmedEmail);
    if (!validationResult.success) {
      const errorKey = validationResult.error?.replace(/^validation\./, "") || "invalidEmail";
      toast.error(t(errorKey as any));
      return;
    }

    // Check if limit reached
    if (referrals.length >= MAX_REFERRALS) {
      toast.error(t("referralLimitReached"), {
        description: t("referralLimitDescription", { count: referrals.length }),
      });
      return;
    }

    setIsSending(true);

    try {
      const response = await fetch("/api/referrals", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(t(data.error as any) || t("failedToSend"));
        return;
      }

      trackSharing("referral_sent");

      toast.success(t("invitationSent", { email: trimmedEmail }), {
        description: t("youGetOneMonthFree"),
      });

      setEmail("");
      fetchReferrals();
    } catch (error) {
      toast.error(t("failedToSend"), {
        description: t("pleaseTryAgain"),
      });
      console.error(error);
    } finally {
      setIsSending(false);
    }
  };

  const handleResend = async (referralId: string, recipientEmail: string) => {
    setResendingId(referralId);

    try {
      const response = await fetch("/api/referrals/resend", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ referralId }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(t(data.error as any) || t("failedToResend"));
        return;
      }

      toast.success(t("invitationResent"), {
        description: `${t("emailDelivered")} - ${recipientEmail}`,
      });
    } catch (error) {
      toast.error(t("failedToResend"), {
        description: t("pleaseTryAgain"),
      });
      console.error(error);
    } finally {
      setResendingId(null);
    }
  };

  const handleDelete = async (referralId: string, recipientEmail: string) => {
    setRemovingId(referralId);

    try {
      const response = await fetch("/api/referrals", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ referralId }),
      });

      if (!response.ok) {
        const data = await response.json();
        toast.error(t(data.error as any) || t("failedToDelete"));
        return;
      }

      // Update UI immediately
      setReferrals((prev) => prev.filter((r) => r.id !== referralId));

      toast.success(t("referralDeleted"), {
        description: t("invitationInvalidated", { email: recipientEmail }),
      });
    } catch (error) {
      toast.error(t("failedToDelete"), {
        description: t("pleaseTryAgain"),
      });
      console.error(error);
    } finally {
      setRemovingId(null);
    }
  };

  const isAnyLoading = isSending || resendingId !== null || removingId !== null;

  return (
    <SettingsSection className="space-y-4">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <UserPlus className="w-4 h-4 text-primary" />
          <h3 className="font-semibold">{t("inviteFriends")}</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{t("inviteFriendsDescription")}</p>
        <Label htmlFor="referral-email" className="text-sm">
          {t("inviteByEmail")}
        </Label>
        <div className="flex gap-2 mt-2">
          <Input
            id="referral-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("emailPlaceholder")}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSendReferral();
              }
            }}
            disabled={isAnyLoading || referrals.length >= MAX_REFERRALS}
          />
          <Button
            onClick={handleSendReferral}
            disabled={isAnyLoading || !email.trim() || referrals.length >= MAX_REFERRALS}
            className="flex-shrink-0 gap-1"
          >
            {isSending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Mail className="w-4 h-4" />
                {t("send")}
              </>
            )}
          </Button>
        </div>
        {referrals.length >= MAX_REFERRALS && (
          <p className="text-xs text-muted-foreground mt-2">
            {t("referralLimitDescription", { count: referrals.length })}
          </p>
        )}
      </div>

      {/* Only show this section after loading completes AND there are referrals */}
      {!isLoadingReferrals && referrals.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">{t("referralsSent")}</h4>
          <div className="space-y-2">
            {referrals.map((referral) => (
              <div
                key={referral.id}
                className="flex items-center justify-between gap-2 p-3 bg-secondary/50 rounded-lg"
              >
                <div className="flex flex-col gap-2 min-w-0 flex-1">
                  <span className="text-sm truncate">{referral.email}</span>
                  {referral.accepted ? (
                    <span className="text-xs text-green-600 dark:text-green-500 rounded-full flex-shrink-0">
                      {t("accepted")}
                    </span>
                  ) : (
                    <span className="text-xs text-yellow-600 dark:text-yellow-500 rounded-full flex-shrink-0">
                      {t("pending")}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {!referral.accepted && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResend(referral.id, referral.email)}
                        disabled={isAnyLoading}
                        title={t("resendInvitation")}
                      >
                        {resendingId === referral.id ? (
                          <Loader2 className="size-5 animate-spin text-muted-foreground" />
                        ) : (
                          <RefreshCw className="size-5 text-muted-foreground" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(referral.id, referral.email)}
                        disabled={isAnyLoading}
                        title={t("deleteInvitation")}
                      >
                        {removingId === referral.id ? (
                          <Loader2 className="size-5 animate-spin text-destructive" />
                        ) : (
                          <Trash2 className="size-5 text-destructive" />
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </SettingsSection>
  );
}
